import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Search, MapPin, Store, BarChart3, Cloud, BrainCircuit, Zap, Shield, Activity, Network, AlertTriangle } from 'lucide-react';
// NOTE: @google/genai is intentionally NOT imported here (Blueprint §00 Dumb Terminal rule).
// The AI brain lives 100% on the central WebSocket server.
// Public mode: connects to /luna WS (no apiKey = Layer 0 public mode)
// Admin mode: LunaVoiceModel handles its own Live API session server-side.
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import StoreDashboard from './components/StoreDashboard';
import AdminDashboard from './components/AdminDashboard';
import { LunaVoiceModel } from './components/LunaVoiceModel';
import { LunaFace } from './components/LunaFace';
import { CheckoutModal } from './components/CheckoutModal';
import POSDashboard from './components/POSDashboard';
import POSCashierLogin, { getCashierSession, clearCashierSession } from './components/POSCashierLogin';
import KangavenaHome from './components/KangavenaHome';
import { ShopSelectorLobby } from './components/ShopSelectorLobby';
import HotelDashboard from './components/HotelDashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AboutUs from './components/AboutUs';
import ReturnPolicy from './components/ReturnPolicy';
import TenetAdminDashboard from './components/tenet/TenetAdminDashboard';
import { KangavenaWhatsAppWeb } from './components/tenet/KangavenaWhatsAppWeb';
import { KangavenaWhatsAppLogin } from './components/auth/KangavenaWhatsAppLogin';
import SystemLoginModal from './components/auth/SystemLoginModal';
import CorporateAdminDashboard from './components/corporate/CorporateAdminDashboard';
import CorporateClientPortal from './components/corporate/CorporateClientPortal';
import { LiveClientPortal } from './components/portal/LiveClientPortal';

type Mode = 'public' | 'admin' | 'store';

// --- Futuristic UI Components ---

const CircuitBoardPattern = () => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="circuit-pattern" width="100" height="100" patternUnits="userSpaceOnUse">
        <path d="M10 10 h20 v20 h20 M50 10 v20 h20 v20 M90 50 h-20 v20 h-20 M10 90 v-20 h20 v-20" fill="none" stroke="currentColor" strokeWidth="1" />
        <circle cx="10" cy="10" r="2" fill="currentColor" />
        <circle cx="50" cy="30" r="2" fill="currentColor" />
        <circle cx="90" cy="50" r="2" fill="currentColor" />
        <circle cx="50" cy="90" r="2" fill="currentColor" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#circuit-pattern)" className="text-cyan-500" />
  </svg>
);

const SpeechBubble = ({ message, isVisible }: { message: string, isVisible: boolean }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.9, x: 20 }}
          animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
          exit={{ opacity: 0, y: 10, scale: 0.9, x: 20 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 200, damping: 20 }}
          className="absolute -top-20 -right-16 z-30 max-w-[200px] pointer-events-none"
        >
          <div className="relative bg-black/40 backdrop-blur-xl border border-white/20 text-white text-xs font-bold tracking-wide px-5 py-3 rounded-2xl rounded-bl-none shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
            {message}
            {/* Tail pointing down-left */}
            <svg className="absolute -bottom-3 left-4 w-5 h-5 text-white/20" viewBox="0 0 10 10">
              <path d="M0 0 L10 0 L0 10 Z" fill="currentColor" />
            </svg>
            <svg className="absolute -bottom-[10px] left-[17px] w-4 h-4 text-black/40 backdrop-blur-xl" viewBox="0 0 10 10">
              <path d="M0 0 L10 0 L0 10 Z" fill="currentColor" />
            </svg>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};



const NeuralBackground = ({ themeColors }: { themeColors: any }) => {
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none transition-colors duration-500" style={{ backgroundColor: themeColors.background }}>
      {/* Deep Space Gradient */}
      <div 
        className="absolute inset-0" 
        style={{ 
          background: `radial-gradient(ellipse at center, ${themeColors.accent}20, transparent 80%)` 
        }} 
      />
      
      {/* Circuit Board Overlay */}
      <CircuitBoardPattern />

      {/* Massive Glowing Orbs (Parallax) */}
      <motion.div style={{ y: y1, backgroundColor: `${themeColors.primary}10` }} className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] blur-[150px] rounded-full mix-blend-screen transition-colors duration-500" />
      <motion.div style={{ y: y2, backgroundColor: `${themeColors.secondary}10` }} className="absolute bottom-[-20%] right-[-10%] w-[900px] h-[900px] blur-[150px] rounded-full mix-blend-screen transition-colors duration-500" />
      <motion.div style={{ backgroundColor: `${themeColors.accent}05` }} className="absolute top-[30%] left-[50%] -translate-x-1/2 w-[1000px] h-[500px] blur-[150px] rounded-full mix-blend-screen transition-colors duration-500" />
      <motion.div style={{ backgroundColor: `${themeColors.primary}05` }} className="absolute top-[60%] left-[20%] w-[600px] h-[600px] blur-[150px] rounded-full mix-blend-screen transition-colors duration-500" />

      {/* Moving Grid */}
      <motion.div 
        className="absolute inset-0 bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)] opacity-10 transition-colors duration-500"
        style={{ backgroundImage: `linear-gradient(${themeColors.accent}33 1px, transparent 1px), linear-gradient(90deg, ${themeColors.accent}33 1px, transparent 1px)` }}
        animate={{ backgroundPosition: ["0px 0px", "100px 100px"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

export default function App() {
  const [mode, setMode] = useState<Mode | 'widget'>(() => {
    const hostname = window.location.hostname;
    const params = new URLSearchParams(window.location.search);
    if (params.has('widget')) return 'widget';
    const isDedicatedRoute = 
      window.location.pathname.startsWith('/tenet') || 
      window.location.pathname.startsWith('/corporate') || 
      window.location.pathname.startsWith('/portal') ||
      window.location.pathname.startsWith('/pos');
    if (!isDedicatedRoute && sessionStorage.getItem('isAdminDashboardMode') === 'true' && !!sessionStorage.getItem('adminToken')) return 'admin';
    if (hostname.startsWith('pos.')) return 'store';
    return 'public';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isLunaConnected, setIsLunaConnected] = useState(false);
  const [lunaStatus, setLunaStatus] = useState('Idle');
  const [connectionStatus, setConnectionStatus] = useState<'Good' | 'Fair' | 'Poor' | 'Checking...'>('Checking...');
  const [sentiment, setSentiment] = useState<'happy' | 'neutral' | 'sad' | 'angry' | 'excited' | 'surprised' | 'concentrating'>('neutral');
  const [apiKey, setApiKey] = useState(() => {
    // Zero-Trust: apiKey lives in sessionStorage (tab-scoped, cleared on browser close).
    // Survives refresh but not close+reopen — close+reopen triggers /api/my-store fetch.
    try { return sessionStorage.getItem('kangavena:storeApiKey') || ''; } catch { return ''; }
  });
  const posSessionStartRef = useRef<number>(0); // epoch ms when manager session starts (for duration calc)
  const [storeName, setStoreName] = useState(() => {
    // Admin route: primary source is pos_admin_session.storeName
    if (window.location.pathname === '/pos/admin') {
      try {
        const s = JSON.parse(localStorage.getItem('pos_admin_session') || 'null');
        if (s?.storeName) return s.storeName;
      } catch {}
    }
    // Fallback for both routes: pos_cashier_session.store.name
    // Handles pre-JWT-migration admin sessions that lacked storeName, or any
    // session where the server returned an empty store name on first admin login.
    try {
      const s = JSON.parse(localStorage.getItem('pos_cashier_session') || 'null');
      if (s?.store?.name) return s.store.name;
    } catch {}
    return '';
  });
  const [activeShopType, setActiveShopType] = useState<string>(() => {
    try {
      const s = JSON.parse(localStorage.getItem('pos_admin_session') || 'null');
      return s?.shopType || 'RETAIL';
    } catch { return 'RETAIL'; }
  });
  const [showShopLobby, setShowShopLobby] = useState(false);
  const [isPosLoginOpen, setIsPosLoginOpen] = useState(false);
  const [posLoginData, setPosLoginData] = useState({ name: '', password: '' });
  const [posLoginError, setPosLoginError] = useState('');
  // POS route detection
  const posPath = window.location.pathname;
  const [isPosDevBypass] = useState(() => posPath === '/pos');
  const [isPosAdminRoute] = useState(() => posPath === '/pos/admin');
  const [isPosAdminAuth, setIsPosAdminAuth] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('pos_admin_session') || 'null');
      // JWT migration: check storeId + storeName, not raw apiKey
      return !!(s?.storeId && s?.storeName);
    } catch { return false; }
  });
  const [isPosAdminLoginError, setIsPosAdminLoginError] = useState('');
  const [posAdminLoginData, setPosAdminLoginData] = useState({ storeCode: '', username: '', password: '' });
  const [isPosAdminLoading, setIsPosAdminLoading] = useState(false);
  const [isCashierRoute] = useState(() => posPath === '/pos/cashier' || posPath === '/pos/login');
  const [cashierSession, setCashierSession] = useState(getCashierSession);
  const [checkoutData, setCheckoutData] = useState<{ productName: string; originalPrice?: string; finalPrice: string } | null>(null);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginError, setAdminLoginError] = useState('');
  const [isAdminLoggingIn, setIsAdminLoggingIn] = useState(false);
  // Fix 1: Admin JWT in sessionStorage (tab-scoped, cleared on browser close) not localStorage
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('adminToken') || '');
  const [dbStatus, setDbStatus] = useState<{status: string, host?: string, error?: string}>({ status: 'checking' });
  const [isDashboardMode, setIsDashboardMode] = useState(() => {
    return window.location.hostname.startsWith('pos.');
  });
  const [isAdminDashboardMode, setIsAdminDashboardMode] = useState(() => {
    if (window.location.pathname.startsWith('/tenet') || 
        window.location.pathname.startsWith('/corporate') || 
        window.location.pathname.startsWith('/portal') ||
        window.location.pathname.startsWith('/whatsapp') ||
        window.location.pathname.startsWith('/wa') ||
        window.location.pathname.startsWith('/pos')) {
      return false;
    }
    return sessionStorage.getItem('isAdminDashboardMode') === 'true' && !!sessionStorage.getItem('adminToken');
  });
  // God Mode is ONLY accessible via SSH tunnel (localhost)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [isPosOnSubdomainEnabled, setIsPosOnSubdomainEnabled] = useState(() => {
    return localStorage.getItem('isPosOnSubdomainEnabled') === 'true';
  });
  const [tenetToken, setTenetToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    const isWhatsAppRoute = window.location.pathname.startsWith('/whatsapp') || window.location.pathname.startsWith('/wa');
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token') || params.get('tenet_token');

    if (isWhatsAppRoute) {
      // Direct tokens from URL on WhatsApp route:
      if (urlToken) {
        if (urlToken.startsWith('pass_')) {
          // Restricted Magic Pass: Handled asynchronously by KangavenaWhatsAppLogin via /api/tenet/magic-passes/verify
          return '';
        } else {
          // Old legacy raw JWT link: strictly reject and purge from URL!
          if (window.history?.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          sessionStorage.setItem(
            'wa_login_error',
            'Legacy access link is expired. Please generate a device-locked Access Pass from Tenet Admin.'
          );
          localStorage.removeItem('tenet_client_token');
          localStorage.removeItem('tenet_token');
          sessionStorage.removeItem('tenet_client_token');
          sessionStorage.removeItem('tenet_token');
          return '';
        }
      }

      // Check stored tokens for WhatsApp route
      const stored =
        sessionStorage.getItem('tenet_token') ||
        sessionStorage.getItem('tenet_client_token') ||
        localStorage.getItem('tenet_client_token') ||
        localStorage.getItem('tenet_token') ||
        '';

      if (!stored || stored.startsWith('pass_')) {
        return '';
      }

      // Verify whether stored token was from credentials or a verified device-locked pass
      try {
        const parts = stored.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const loginType =
            localStorage.getItem('tenet_login_type') || sessionStorage.getItem('tenet_login_type');
          const isVerifiedPass = !!(payload.passId || payload.isMagicPass);
          const isCreds = loginType === 'credentials';

          if (!isVerifiedPass && !isCreds) {
            // Old raw link token stored from prior sessions: purge it!
            localStorage.removeItem('tenet_client_token');
            localStorage.removeItem('tenet_token');
            sessionStorage.removeItem('tenet_client_token');
            sessionStorage.removeItem('tenet_token');
            sessionStorage.setItem(
              'wa_login_error',
              'Previous session from legacy link has been revoked. Please use a device-locked Access Pass.'
            );
            return '';
          }
        }
      } catch (e) {
        return '';
      }

      return stored;
    }

    if (urlToken) {
      sessionStorage.setItem('tenet_token', urlToken);
      sessionStorage.setItem('tenet_client_token', urlToken);
      localStorage.setItem('tenet_client_token', urlToken);
      return urlToken;
    }
    const stored = sessionStorage.getItem('tenet_token') || 
           sessionStorage.getItem('tenet_client_token') || 
           localStorage.getItem('tenet_client_token') || 
           localStorage.getItem('tenet_token') || 
           sessionStorage.getItem('tenet_auth_token') || 
           '';
    if (stored.startsWith('pass_')) {
      localStorage.removeItem('tenet_client_token');
      localStorage.removeItem('tenet_token');
      sessionStorage.removeItem('tenet_client_token');
      sessionStorage.removeItem('tenet_token');
      return '';
    }
    return stored;
  });
  const [corporateAdminToken, setCorporateAdminToken] = useState(() => localStorage.getItem('corporate_admin_token') || '');
  const [corporateUserToken, setCorporateUserToken] = useState(() => localStorage.getItem('corporate_user_token') || '');
  const [isSystemLoginOpen, setIsSystemLoginOpen] = useState(() => typeof window !== 'undefined' && (window.location.pathname === '/login' || window.location.pathname === '/register'));
  const [systemLoginInitialTab, setSystemLoginInitialTab] = useState<'pos' | 'tenet' | 'corporate' | 'tenet_register'>(() => typeof window !== 'undefined' && window.location.pathname === '/register' ? 'tenet_register' : 'pos');

  useEffect(() => {
    localStorage.setItem('isPosOnSubdomainEnabled', isPosOnSubdomainEnabled.toString());
  }, [isPosOnSubdomainEnabled]);

  // Track whether /api/my-store session restoration is in progress (true = blocking render)
  const [apiKeyRestoring, setApiKeyRestoring] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isAdminOrCashier = window.location.pathname === '/pos/admin' || window.location.pathname === '/pos/cashier' || window.location.pathname === '/pos/login';
    if (!isAdminOrCashier) return false;
    return !!(localStorage.getItem('kangavena:cashierToken')); // true only if we have a token to restore
  });

  // JWT migration: on page refresh, restore apiKey in React state from /api/my-store
  // apiKey is no longer stored in localStorage — the JWT alone is sufficient to fetch it
  // Includes retry logic (5 attempts, 2s apart) to survive server restarts.
  useEffect(() => {
    if (!isPosAdminRoute && !isCashierRoute) return;
    if (apiKey) { setApiKeyRestoring(false); return; } // already set (just logged in)
    if (!navigator.onLine) { setApiKeyRestoring(false); return; }
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) {
      setApiKeyRestoring(false);
      // No JWT on admin route — stale pos_admin_session without a token means we can't
      // authenticate checkouts. Force re-login so a fresh ownerToken is issued.
      if (isPosAdminRoute && isPosAdminAuth) {
        localStorage.removeItem('pos_admin_session');
        setIsPosAdminAuth(false);
      }
      return;
    }

    let attempt = 0;
    const MAX_ATTEMPTS = 6;
    const RETRY_MS = 2500;
    let cancelled = false;

    const tryFetch = () => {
      if (cancelled) return;
      fetch('/api/my-store', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => {
          if (r.status === 401) {
            // Stale / expired / wrong-secret token — clear and stop retrying.
            localStorage.removeItem('kangavena:cashierToken');
            // On the admin route, also clear the stale session so the login form shows.
            if (isPosAdminRoute) {
              localStorage.removeItem('pos_admin_session');
              setIsPosAdminAuth(false);
            }
            setApiKeyRestoring(false);
            return null;
          }
          return r.ok ? r.json() : null;
        })
        .then(data => {
          if (cancelled) return;
          if (data?.store?.apiKey) {
            setApiKey(data.store.apiKey);
            sessionStorage.setItem('kangavena:storeApiKey', data.store.apiKey);
            if (!storeName && data.store.name) setStoreName(data.store.name);
            if (!activeShopType || activeShopType === 'RETAIL') setActiveShopType(data.store.shopType || 'RETAIL');
            if (posSessionStartRef.current === 0) posSessionStartRef.current = Date.now(); // seed for restored sessions
            setApiKeyRestoring(false);
          } else {
            // No data yet — retry if attempts remain (server still starting up after restart)
            attempt++;
            if (attempt < MAX_ATTEMPTS) setTimeout(tryFetch, RETRY_MS);
            else {
              // Do NOT dismiss the loading screen on exhaustion — the POS UI must not
              // render with an empty apiKey. The outer render gate (isPosAdminAuth && !apiKey)
              // will keep showing the restoring screen with a retry button.
              setApiKeyRestoring(false);
            }
          }
        })
        .catch(() => {
          if (cancelled) return;
          attempt++;
          if (attempt < MAX_ATTEMPTS) setTimeout(tryFetch, RETRY_MS);
          else {
            // Do NOT dismiss the loading screen on exhaustion — see comment above.
            setApiKeyRestoring(false);
          }
        });
    };
    tryFetch();
    return () => { cancelled = true; };
  }, [setApiKeyRestoring, setApiKey, setStoreName, setActiveShopType]);

  // POS Activity Log: beacon SESSION_END when tab closes / refreshes / navigates away.
  // Uses sendBeacon so it fires reliably on unload without blocking the page exit.
  useEffect(() => {
    if (!isPosAdminRoute) return;
    const onUnload = () => {
      const token = localStorage.getItem('kangavena:cashierToken');
      if (!token) return;
      const dur = posSessionStartRef.current > 0 ? Math.round((Date.now() - posSessionStartRef.current) / 1000) : 0;
      if (dur < 5) return; // ignore accidental tab flickers
      let sn = '';
      try { sn = JSON.parse(localStorage.getItem('pos_admin_session') || '{}')?.storeName || ''; } catch {}
      const blob = new Blob([JSON.stringify({ token, eventType: 'SESSION_END', durationSeconds: dur, storeName: sn, role: 'manager' })], { type: 'application/json' });
      navigator.sendBeacon('/api/pos/activity-log-beacon', blob);
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [isPosAdminRoute]); // posSessionStartRef is a stable ref — no re-run needed

  const [themeColors, setThemeColors] = useState({
    background: '#000000',
    text: '#ffffff',
    accent: '#3b82f6',
    primary: '#10b981',
    secondary: '#8b5cf6'
  });

  // ── GOD MODE: Secret URL access (secure — works from anywhere) ──
  const GOD_MODE_SECRET_PATH = '/x/NLRltDYrciPtEKlXQkIG37kyDu9OBM7X';
  useEffect(() => {
    // SECURITY: The secret path reveals the login modal, but authentication still requires:
    // 1. Admin password (bcrypt + rate-limited + account lockout)
    // 2. God Mode PIN (constant-time comparison, 30min session)
    // 3. JWT expires in 2h, blacklisted on logout
    // The path alone grants nothing — it just shows the login form.
    if (window.location.pathname === GOD_MODE_SECRET_PATH) {
      setIsAdminLoginOpen(true);
      // Replace URL to hide the secret path from address bar
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Luna Jedi Mode — modal trigger listener
  useEffect(() => {
    const handleLunaModal = (e: any) => {
      const { modalName, productName, originalPrice, finalPrice } = e.detail || {};
      if (modalName === 'pos_login') setIsPosLoginOpen(true);
      else if (modalName === 'admin_login') setIsAdminLoginOpen(true);
      else if (modalName === 'checkout') {
        setCheckoutData({
          productName: productName || 'Selected Product',
          originalPrice: originalPrice || undefined,
          finalPrice: finalPrice || 'Contact for price',
        });
      }
    };
    window.addEventListener('luna-trigger-modal', handleLunaModal);
    return () => window.removeEventListener('luna-trigger-modal', handleLunaModal);
  }, []);

  // Pre-load voices for SpeechSynthesis to ensure natural female voice is available
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices(); // Trigger load
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  useEffect(() => {
    // Only poll /api/db-status when an admin session exists (endpoint requires requireAdminAuth)
    if (!adminToken) return;
    const checkStatus = async () => {
      try {
        const res = await fetch('/api/db-status', { headers: { 'Authorization': `Bearer ${adminToken}` } });
        if (!res.ok) return; // silently ignore 401 — admin token may have just expired
        const data = await res.json();
        setDbStatus(data);
      } catch (e) {
        setDbStatus({ status: 'disconnected', error: 'Could not reach server' });
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 10000); // 10s is sufficient for a status check
    return () => clearInterval(interval);
  }, [adminToken]);

  const DevBox = ({ status }: { status: any }) => null;

  const changeThemeColor = (element: string, color: string) => {
    if (mode === 'widget') {
      window.parent.postMessage({ type: 'LUNA_ACTION', action: 'CHANGE_THEME_COLOR', payload: { element, color } }, '*');
      return `Requested parent website to change ${element} color to ${color}`;
    }
    const validElements = ['background', 'text', 'accent', 'primary', 'secondary'];
    if (validElements.includes(element)) {
      console.log(`Applying color: ${color} to ${element}`);
      setThemeColors(prev => {
        const next = { ...prev, [element]: color };
        console.log("New theme colors state:", next);
        return next;
      });
      return `Changed ${element} color to ${color}`;
    }
    return `Error: Invalid element ${element}. Valid elements are: ${validElements.join(', ')}`;
  };

  const triggerCelebration = () => {
    if (mode === 'widget') {
      window.parent.postMessage({ type: 'LUNA_ACTION', action: 'TRIGGER_CELEBRATION' }, '*');
      return "Requested parent website to trigger celebration!";
    }
    const duration = 3 * 1000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: [themeColors.primary, themeColors.secondary, themeColors.accent]
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: [themeColors.primary, themeColors.secondary, themeColors.accent]
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
    return "Celebration triggered successfully!";
  };

  const scrollToSection = (sectionId: string) => {
    if (mode === 'widget') {
      window.parent.postMessage({ type: 'LUNA_ACTION', action: 'SCROLL_TO_SECTION', payload: { sectionId } }, '*');
      return `Requested parent website to scroll to section: ${sectionId}`;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      return `Scrolled to section: ${sectionId}`;
    }
    return `Error: Section ${sectionId} not found.`;
  };

  const showPopup = (title: string, content: string) => {
    if (mode === 'widget') {
      window.parent.postMessage({ type: 'LUNA_ACTION', action: 'SHOW_POPUP', payload: { title, content } }, '*');
      return `Requested parent website to show popup: ${title}`;
    }
    return `Error: Popups are only supported in widget mode.`;
  };

  const [messageQueue, setMessageQueue] = useState<string[]>([]);
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);

  useEffect(() => {
    if (lunaStatus === 'Listening' || lunaStatus === 'Speaking') {
      // If we've transitioned to an active state, clear any pending status messages
      if (currentMessage === 'Connecting...' || currentMessage === 'Processing...') {
        setCurrentMessage(null);
      }
    }
    
    if (lunaStatus !== 'Idle' && lunaStatus !== 'Listening' && lunaStatus !== 'Speaking') {
      setMessageQueue(prev => [...prev, lunaStatus]);
    }
  }, [lunaStatus]);

  useEffect(() => {
    if (messageQueue.length > 0 && !currentMessage) {
      setCurrentMessage(messageQueue[0]);
      setMessageQueue(prev => prev.slice(1));
      
      const timer = setTimeout(() => {
        setCurrentMessage(null);
      }, 2500); // Show each message for 2.5 seconds
      return () => clearTimeout(timer);
    }
  }, [messageQueue, currentMessage]);

  // Stats
  const [adminStats, setAdminStats] = useState<any>(null);
  const [storeStats, setStoreStats] = useState<any>(null);

  useEffect(() => {
    const checkLatency = async () => {
      const start = Date.now();
      try {
        await fetch('/api/health');
        const latency = Date.now() - start;
        if (latency < 200) setConnectionStatus('Good');
        else if (latency < 500) setConnectionStatus('Fair');
        else setConnectionStatus('Poor');
      } catch (e) {
        setConnectionStatus('Poor');
      }
    };
    checkLatency();
    const interval = setInterval(checkLatency, 5000);
    return () => clearInterval(interval);
  }, []);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sessionRef = useRef<any>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  
  // Ambient Sound Refs
  const ambientOscRef = useRef<OscillatorNode | null>(null);
  const ambientGainRef = useRef<GainNode | null>(null);
  const ambientLfoRef = useRef<OscillatorNode | null>(null);

  // UI Sound Helper
  const playUISound = (type: 'ping' | 'warning' | 'emotion' | 'slow' | 'idle' | 'listening', emotion?: string) => {
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    
    const createOsc = (freq: number, type: OscillatorType, startTime: number, duration: number, startGain: number, endGain = 0.001) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(startGain, startTime);
      gain.gain.exponentialRampToValueAtTime(endGain, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    if (type === 'ping') {
      createOsc(880, 'sine', now, 0.1, 0.08);
      createOsc(1320, 'sine', now + 0.05, 0.15, 0.04);
    } else if (type === 'warning') {
      createOsc(150, 'sine', now, 0.4, 0.08);
      createOsc(100, 'sine', now + 0.1, 0.4, 0.04);
    } else if (type === 'emotion') {
      switch (emotion) {
        case 'happy':
          createOsc(523.25, 'sine', now, 0.4, 0.06); // C5
          createOsc(659.25, 'sine', now + 0.1, 0.4, 0.04); // E5
          break;
        case 'excited':
          createOsc(523.25, 'sine', now, 0.2, 0.08); // C5
          createOsc(659.25, 'sine', now + 0.04, 0.2, 0.06); // E5
          createOsc(783.99, 'sine', now + 0.08, 0.2, 0.04); // G5
          createOsc(1046.50, 'sine', now + 0.12, 0.2, 0.03); // C6
          break;
        case 'sad':
          createOsc(392.00, 'sine', now, 0.8, 0.05); // G4
          createOsc(466.16, 'sine', now + 0.15, 0.8, 0.03); // Bb4 (Minor)
          break;
        case 'angry':
          createOsc(220, 'sawtooth', now, 0.08, 0.03);
          createOsc(233.08, 'sawtooth', now + 0.04, 0.08, 0.02); // Dissonant
          break;
        case 'surprised':
          const oscS = ctx.createOscillator();
          const gainS = ctx.createGain();
          oscS.frequency.setValueAtTime(440, now);
          oscS.frequency.exponentialRampToValueAtTime(880, now + 0.15);
          gainS.gain.setValueAtTime(0.04, now);
          gainS.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
          oscS.connect(gainS);
          gainS.connect(ctx.destination);
          oscS.start(now);
          oscS.stop(now + 0.15);
          break;
        case 'concentrating':
          // Soft rhythmic pulses
          createOsc(440, 'sine', now, 0.1, 0.03);
          createOsc(440, 'sine', now + 0.2, 0.1, 0.02);
          createOsc(440, 'sine', now + 0.4, 0.1, 0.01);
          break;
        default:
          createOsc(440, 'sine', now, 0.2, 0.04);
      }
    } else if (type === 'slow') {
      createOsc(80, 'sine', now, 0.2, 0.06);
    } else if (type === 'listening') {
      // Very soft high tick
      createOsc(2500, 'sine', now, 0.03, 0.015);
    } else if (type === 'idle') {
      // Soft downward sigh
      const oscI = ctx.createOscillator();
      const gainI = ctx.createGain();
      oscI.frequency.setValueAtTime(180, now);
      oscI.frequency.linearRampToValueAtTime(90, now + 0.4);
      gainI.gain.setValueAtTime(0.02, now);
      gainI.gain.linearRampToValueAtTime(0, now + 0.4);
      oscI.connect(gainI);
      gainI.connect(ctx.destination);
      oscI.start(now);
      oscI.stop(now + 0.4);
    }
  };

  const stopAmbientSound = (fadeOut = 0.5) => {
    if (ambientGainRef.current && audioContextRef.current) {
      const now = audioContextRef.current.currentTime;
      const gain = ambientGainRef.current;
      const osc = ambientOscRef.current;
      const lfo = ambientLfoRef.current;

      gain.gain.cancelScheduledValues(now);
      gain.gain.linearRampToValueAtTime(0, now + fadeOut);
      
      setTimeout(() => {
        try { osc?.stop(); } catch(e) {}
        try { lfo?.stop(); } catch(e) {}
        osc?.disconnect();
        lfo?.disconnect();
        gain?.disconnect();
        
        if (ambientOscRef.current === osc) ambientOscRef.current = null;
        if (ambientLfoRef.current === lfo) ambientLfoRef.current = null;
        if (ambientGainRef.current === gain) ambientGainRef.current = null;
      }, (fadeOut * 1000) + 100);
    }
  };

  const startAmbientWoob = () => {
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 60;
    lfo.type = 'sine';
    lfo.frequency.value = 0.5;
    lfoGain.gain.value = 20;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 0.5);
    
    osc.start();
    lfo.start();
    
    ambientOscRef.current = osc;
    ambientLfoRef.current = lfo;
    ambientGainRef.current = gain;
  };

  // Slow Connection Sound Monitor
  useEffect(() => {
    if ((connectionStatus === 'Poor' || connectionStatus === 'Fair') && isLunaConnected) {
      playUISound('slow');
    }
  }, [connectionStatus, isLunaConnected]);

  useEffect(() => {
    if (mode === 'admin') {
      fetch('/api/admin/stats').then(res => res.ok ? res.json() : null).then(setAdminStats).catch(() => {});
    } else if (mode === 'store') {
      fetch('/api/store/s3/stats').then(res => res.json()).then(setStoreStats);
    }
  }, [mode]);

  useEffect(() => {
    if (lunaStatus === 'Listening') {
      playUISound('listening');
    } else if (lunaStatus === 'Idle') {
      playUISound('idle');
    }
  }, [lunaStatus]);

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!searchQuery) return;
    const res = await fetch(`/api/inventory?q=${searchQuery}`);
    const data = await res.json();
    setSearchResults(data);
    
    if (sessionRef.current && isListening) {
      sessionRef.current.then((session: any) => {
        const resultsText = data.map((item: any) => 
          `${item.name} is available at ${item.store?.name} in ${item.store?.location} for Rs. ${item.price}`
        ).join('. ');
        session.sendRealtimeInput({
          text: `I found these products: ${resultsText}. Please tell the user about these stores and locations.`
        });
      });
    }
  };

  const isStartingRef = useRef(false);
  // WebSocket ref for public mode backend connection (Blueprint §00 Dumb Terminal)
  const publicWsRef = useRef<WebSocket | null>(null);
  // SpeechRecognition ref for capturing user voice input
  const recognitionRef = useRef<any>(null);

  const startLuna = async () => {
    if (isStartingRef.current || lunaStatus !== 'Idle') return;
    isStartingRef.current = true;
    setLunaStatus('Connecting...');
    setMessageQueue(['Connecting...']);

    try {
      // Init AudioContext for UI sounds
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      await audioContextRef.current.resume();
      startAmbientWoob();

      // ================================================================
      // BLUEPRINT §00 DUMB TERMINAL — Public mode connects to BACKEND WS
      // No apiKey = Layer 0 (public home mode) on server
      // ================================================================
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${protocol}://${window.location.host}/luna`;
      const ws = new WebSocket(wsUrl);
      publicWsRef.current = ws;

      ws.onopen = () => {
        console.log('[Luna WS] Connected to backend (Layer 0 public mode)');
        setIsLunaConnected(true);
        setLunaStatus('Listening');
        setIsListening(true);
        setMessageQueue([]);
        isStartingRef.current = false;
        playUISound('ping');
        stopAmbientSound(0.1);
        startVoiceRecognition(ws);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.message) {
            setCurrentMessage(data.message);
            setMessageQueue([]);
            // Blueprint §08 Widget pattern: speak via window.speechSynthesis (female voice)
            speakText(data.message);
            setTimeout(() => setCurrentMessage(null), 8000);
          }
          if (data.emotion) {
            setSentiment(data.emotion as any);
            playUISound('emotion', data.emotion);
          }
        } catch (e) {
          console.error('[Luna WS] Message parse error:', e);
        }
      };

      ws.onerror = (err) => {
        console.error('[Luna WS] Error:', err);
        setLunaStatus('Idle');
        isStartingRef.current = false;
        stopLuna();
      };

      ws.onclose = () => {
        console.log('[Luna WS] Connection closed');
        setIsLunaConnected(false);
        isStartingRef.current = false;
        stopLuna();
      };
    } catch (err) {
      console.error('[Luna WS] Error starting:', err);
      setLunaStatus('Idle');
      isStartingRef.current = false;
    }
  };

  // Blueprint §08 — Speak via browser SpeechSynthesis with female voice
  const speakText = (text: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    setLunaStatus('Speaking');
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.1;
    utterance.volume = 1.0;
    // Pick a natural female voice if available
    let voices = window.speechSynthesis.getVoices();
    // Sometimes voices array is empty immediately, we wait for it
    if (voices.length === 0) {
      console.warn('[Luna TTS] Voices not loaded yet, using fallback.');
    }
    
    // Explicitly select natural female voices
    const femaleVoice = voices.find(v => {
      const name = v.name.toLowerCase();
      // Prioritize high-quality voices
      if (name.includes('google us english') && name.includes('female')) return true;
      if (name.includes('zira')) return true;
      if (name.includes('samantha')) return true;
      if (name.includes('fiona')) return true;
      if (name.includes('google uk english female')) return true;
      if (name.includes('sinhala') && name.includes('female')) return true;
      return false;
    }) || voices.find(v => v.name.toLowerCase().includes('female')); // Fallback to any female voice

    if (femaleVoice) {
      utterance.voice = femaleVoice;
    } else {
      console.warn('[Luna TTS] No explicit female voice found. Falling back to default.');
    }
    utterance.onend = () => {
      setLunaStatus('Listening');
    };
    window.speechSynthesis.speak(utterance);
  };

  // Start browser SpeechRecognition to capture voice → send text to backend WS
  const startVoiceRecognition = (ws: WebSocket) => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('[Luna] SpeechRecognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US'; // Also accepts Sinhala phrases in mixed mode

    recognition.onresult = (event: any) => {
      const transcript = event.results[event.results.length - 1][0].transcript;
      console.log('[Luna STT] Heard:', transcript);
      if (ws.readyState === WebSocket.OPEN) {
        setLunaStatus('Thinking...');
        setCurrentMessage(`You: "${transcript}"`);
        ws.send(JSON.stringify({ message: transcript }));
      }
    };

    recognition.onerror = (e: any) => {
      console.warn('[Luna STT] Recognition error:', e.error);
    };

    recognition.onend = () => {
      // Auto-restart recognition so listening is continuous
      if (publicWsRef.current?.readyState === WebSocket.OPEN) {
        try { recognition.start(); } catch (e) {}
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  const stopLuna = () => {
    setIsListening(false);
    setIsLunaConnected(false);
    setLunaStatus('Idle');
    setSentiment('neutral');
    setCurrentMessage(null);
    setMessageQueue([]);
    nextPlayTimeRef.current = 0;
    activeSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
    activeSourcesRef.current = [];

    // Stop ambient sound if running
    stopAmbientSound(0.1);

    // Stop SpeechSynthesis
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();

    // Stop SpeechRecognition
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
    }

    // Close backend WebSocket
    if (publicWsRef.current) {
      if (publicWsRef.current.readyState === WebSocket.OPEN) {
        publicWsRef.current.close();
      }
      publicWsRef.current = null;
    }

    // Close AudioContext (for UI sounds)
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    isStartingRef.current = false;
  };








  const getSentimentStyles = () => {
    if (!isListening) return { 
      ring1: 'border-cyan-500/20', 
      ring2: 'border-purple-500/10', 
      ring3: 'border-emerald-500/10', 
      core: 'bg-cyan-500/20', 
      buttonBorder: 'border-white/20', 
      buttonShadow: 'shadow-[0_0_50px_rgba(6,182,212,0.4)]', 
      buttonHover: 'hover:border-cyan-400 hover:shadow-[0_0_80px_rgba(6,182,212,0.6)]',
      icon: 'text-cyan-400 drop-shadow-[0_0_20px_rgba(6,182,212,1)]'
    };

    switch (sentiment) {
      case 'happy': return {
        ring1: 'border-amber-400/60', ring2: 'border-yellow-300/40', ring3: 'border-orange-400/30',
        core: 'bg-amber-400/50', buttonBorder: 'border-amber-400/80', buttonShadow: 'shadow-[0_0_100px_rgba(251,191,36,0.8)]', buttonHover: '',
        icon: 'text-amber-300 drop-shadow-[0_0_25px_rgba(251,191,36,1)]'
      };
      case 'excited': return {
        ring1: 'border-pink-500/80', ring2: 'border-fuchsia-500/60', ring3: 'border-rose-500/40',
        core: 'bg-pink-500/60', buttonBorder: 'border-pink-500/90', buttonShadow: 'shadow-[0_0_120px_rgba(236,72,153,1)]', buttonHover: '',
        icon: 'text-pink-300 drop-shadow-[0_0_35px_rgba(236,72,153,1)]'
      };
      case 'sad': return {
        ring1: 'border-blue-600/80', ring2: 'border-indigo-600/60', ring3: 'border-sky-600/40',
        core: 'bg-blue-600/60', buttonBorder: 'border-blue-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(37,99,235,1)]', buttonHover: '',
        icon: 'text-blue-300 drop-shadow-[0_0_35px_rgba(37,99,235,1)]'
      };
      case 'angry': return {
        ring1: 'border-red-600/80', ring2: 'border-orange-600/60', ring3: 'border-rose-600/40',
        core: 'bg-red-600/60', buttonBorder: 'border-red-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(220,38,38,1)]', buttonHover: '',
        icon: 'text-red-400 drop-shadow-[0_0_35px_rgba(220,38,38,1)]'
      };
      case 'surprised': return {
        ring1: 'border-emerald-400/80', ring2: 'border-teal-400/60', ring3: 'border-cyan-400/40',
        core: 'bg-emerald-400/60', buttonBorder: 'border-emerald-400/90', buttonShadow: 'shadow-[0_0_120px_rgba(52,211,153,1)]', buttonHover: '',
        icon: 'text-emerald-300 drop-shadow-[0_0_35px_rgba(52,211,153,1)]'
      };
      case 'concentrating': return {
        ring1: 'border-violet-600/80', ring2: 'border-purple-600/60', ring3: 'border-fuchsia-600/40',
        core: 'bg-violet-600/60', buttonBorder: 'border-violet-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(124,58,237,1)]', buttonHover: '',
        icon: 'text-violet-300 drop-shadow-[0_0_35px_rgba(124,58,237,1)]'
      };
      default: return { // neutral
        ring1: 'border-cyan-400/60', ring2: 'border-teal-400/40', ring3: 'border-emerald-400/30',
        core: 'bg-cyan-400/50', buttonBorder: 'border-cyan-400/80', buttonShadow: 'shadow-[0_0_100px_rgba(34,211,238,0.8)]', buttonHover: '',
        icon: 'text-cyan-300 drop-shadow-[0_0_25px_rgba(34,211,238,1)]'
      };
    }
  };
  const sStyles = getSentimentStyles();

  if (mode === 'widget') {
    const params = new URLSearchParams(window.location.search);
    const widgetApiKey = params.get('apiKey') || 'TECH_123';
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
        <NeuralBackground themeColors={themeColors} />
        <div className="w-full h-full">
          <LunaVoiceModel apiKey={widgetApiKey} role="tenant" />
        </div>
      </div>
    );
  }

  // /pos — redirect to /pos/admin (dev bypass removed)
  if (isPosDevBypass) {
    window.location.replace('/pos/admin');
    return null;
  }

  // /pos/admin — Manager (full manage mode with Luna + inventory CRUD)
  if (isPosAdminRoute) {
    // Session restoring: manager token exists but apiKey not yet resolved (e.g. after server restart).
    // Show a minimal loading screen instead of a broken dashboard. Auto-dismisses once apiKey is set.
    // CRITICAL FIX: Never render the POS dashboard until apiKey is hydrated.
    // The apiKey is restored from sessionStorage (survives refresh) or fetched
    // via /api/my-store (browser close+reopen). If the fetch exhausts retries,
    // we show a retry button instead of a broken dashboard.
    if (isPosAdminAuth && !apiKey) {
      const canRetry = navigator.onLine && !apiKeyRestoring;
      return (
        <div className="min-h-screen bg-[#0b0f1a] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className={`w-10 h-10 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center ${!canRetry ? 'animate-pulse' : ''}`}>
              <span className="text-violet-400 text-lg">⚡</span>
            </div>
            <p className="text-slate-400 text-sm font-mono">
              {navigator.onLine ? 'Restoring session…' : 'You are offline'}
            </p>
            <p className="text-slate-600 text-[10px] font-mono max-w-[240px] text-center">
              {navigator.onLine
                ? (canRetry
                  ? 'Could not reach the server. Click below to retry.'
                  : 'Reconnecting to server')
                : 'Please reconnect to the internet to restore your store context.'}
            </p>
            {canRetry && (
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-4 py-2 rounded-xl bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs font-bold hover:bg-violet-500/30 transition-colors"
              >
                Retry Connection
              </button>
            )}
          </div>
        </div>
      );
    }
    if (!isPosAdminAuth) {
      const handleAdminPosLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsPosAdminLoading(true);
        setIsPosAdminLoginError('');
        try {
          const res = await fetch('/api/pos-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              storeCode: posAdminLoginData.storeCode.trim().toUpperCase(),
              username: posAdminLoginData.username.trim(),
              password: posAdminLoginData.password,
            }),
          });
          const data = await res.json();
          if (!res.ok) { setIsPosAdminLoginError(data.error || 'Login failed'); return; }
          // JWT migration: store only storeId + storeName + shopType — no raw apiKey in localStorage
          const session = { storeName: data.store.name, shopType: data.store.shopType || 'RETAIL', storeId: data.store.id };
          localStorage.setItem('pos_admin_session', JSON.stringify(session));
          if (data.ownerToken) {
            localStorage.setItem('kangavena:cashierToken', data.ownerToken);
          }
          setStoreName(data.store.name);
          setApiKey(data.store.apiKey);
          sessionStorage.setItem('kangavena:storeApiKey', data.store.apiKey);
          setActiveShopType(data.store.shopType || 'RETAIL');
          setIsPosAdminAuth(true);
          // POS Activity Log: LOGIN event (fire-and-forget)
          posSessionStartRef.current = Date.now();
          fetch('/api/pos/activity-log', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.ownerToken}` },
            body: JSON.stringify({ eventType: 'LOGIN', storeName: data.store.name, role: 'manager' }),
          }).catch(() => {});
        } catch { setIsPosAdminLoginError('Cannot reach server'); }
        finally { setIsPosAdminLoading(false); }
      };
      return (
        <div className="min-h-screen bg-[#04060f] flex flex-col items-center justify-center p-4 relative overflow-hidden">
          <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-500/6 blur-[140px]" />
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm relative z-10">
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <Zap size={12} className="text-violet-400" />
                <span className="text-[11px] font-black tracking-[0.2em] text-violet-300 uppercase">POS.OS · Manager Login</span>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl">
              <div className="px-6 pt-6 pb-2">
                <div className="w-12 h-12 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mb-4">
                  <Shield size={22} className="text-violet-400" />
                </div>
                <h1 className="text-xl font-black text-white mb-1">Manager Access</h1>
                <p className="text-[11px] text-slate-600 font-mono">Full inventory control, cashier management, and Luna AI</p>
              </div>
              <form onSubmit={handleAdminPosLogin} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Store Code</label>
                  <input value={posAdminLoginData.storeCode}
                    onChange={e => setPosAdminLoginData(p => ({ ...p, storeCode: e.target.value.toUpperCase() }))}
                    placeholder="e.g. TECH or RAT01" required maxLength={16}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[14px] text-violet-300 placeholder:text-slate-700 outline-none focus:border-violet-500/40 font-mono tracking-[0.2em] uppercase" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Username</label>
                  <input value={posAdminLoginData.username} onChange={e => setPosAdminLoginData(p => ({ ...p, username: e.target.value }))}
                    placeholder="Your admin username" required
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-violet-500/40 font-mono" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Master Password</label>
                  <input value={posAdminLoginData.password} onChange={e => setPosAdminLoginData(p => ({ ...p, password: e.target.value }))}
                    type="password" placeholder="••••••••"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-violet-500/40 font-mono" />
                </div>
                {isPosAdminLoginError && (
                  <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-red-500/8 border border-red-500/20 text-red-400 text-[11px] font-mono">
                    <AlertTriangle size={12} /> {isPosAdminLoginError}
                  </div>
                )}
                <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={isPosAdminLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[12px] uppercase tracking-widest bg-gradient-to-r from-violet-500 to-purple-600 text-white disabled:opacity-40 shadow-[0_0_24px_rgba(139,92,246,0.25)] hover:shadow-[0_0_36px_rgba(139,92,246,0.4)] transition-shadow">
                  {isPosAdminLoading ? 'Signing in…' : 'Enter Manager Mode'}
                </motion.button>
              </form>
              <div className="px-6 pb-5 text-center">
                <p className="text-[9px] text-slate-700 font-mono">Cashier? <a href="/pos/cashier" className="text-amber-500 hover:text-amber-400">/pos/cashier</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      );
    }
    const handleShopSwitch = (shop: { apiKey: string; name: string; shopType: string; storeId: number }) => {
      // JWT migration: no apiKey in localStorage
      const session = { storeName: shop.name, shopType: shop.shopType, storeId: shop.storeId };
      localStorage.setItem('pos_admin_session', JSON.stringify(session));
      setApiKey(shop.apiKey);
      sessionStorage.setItem('kangavena:storeApiKey', shop.apiKey);
      setStoreName(shop.name);
      setActiveShopType(shop.shopType);
      setShowShopLobby(false);
    };

    const handleManagerLogout = () => {
      // POS Activity Log: LOGOUT event with session duration
      const token = localStorage.getItem('kangavena:cashierToken');
      if (token) {
        const dur = posSessionStartRef.current > 0 ? Math.round((Date.now() - posSessionStartRef.current) / 1000) : 0;
        fetch('/api/pos/activity-log', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ eventType: 'LOGOUT', storeName, role: 'manager', durationSeconds: dur }),
        }).catch(() => {});
      }
      localStorage.removeItem('pos_admin_session');
      localStorage.removeItem('kangavena:cashierToken');
      sessionStorage.removeItem('kangavena:storeApiKey');
      window.location.assign('/pos/admin');
    };

    if (showShopLobby) {
      return (
        <ShopSelectorLobby
          currentApiKey={apiKey}
          currentStoreName={storeName || 'Store'}
          onSelectShop={handleShopSwitch}
          onLogout={handleManagerLogout}
        />
      );
    }

    if (activeShopType === 'HOTEL') {
      return (
        <HotelDashboard
          storeApiKey={apiKey}
          storeName={storeName || 'Hotel'}
          onSwitchShop={() => setShowShopLobby(true)}
          onExit={handleManagerLogout}
        />
      );
    }

    return (
      <POSDashboard
        storeApiKey={apiKey}
        storeName={storeName || 'Store'}
        cashierName="Manager"
        cashierMode={false}
        onSwitchShop={() => setShowShopLobby(true)}
        onExit={handleManagerLogout}
      />
    );
  }

  // /pos/cashier — Cashier (strict cashier mode, no Luna)
  if (isCashierRoute) {
    if (!cashierSession) {
      return <POSCashierLogin onSuccess={session => {
        setCashierSession(session);
        setStoreName(session.store.name);
        // JWT migration: apiKey no longer on session — fetch from /api/my-store via the saved JWT
        const savedToken = localStorage.getItem('kangavena:cashierToken');
        if (savedToken) {
          fetch('/api/my-store', { headers: { 'Authorization': `Bearer ${savedToken}` } })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
              if (d?.store?.apiKey) {
                setApiKey(d.store.apiKey);
                sessionStorage.setItem('kangavena:storeApiKey', d.store.apiKey);
              }
            })
            .catch(() => {});
        }
      }} />;
    }
    return (
      <POSDashboard
        storeApiKey={apiKey || ''}
        storeName={cashierSession.store.name}
        cashierName={cashierSession.cashier.name}
        cashierMode={true}
        onExit={() => { clearCashierSession(); setCashierSession(null); }}
      />
    );
  }

  if (isDashboardMode) {
    return (
      <>
        <StoreDashboard onExit={() => {
          if (window.location.hostname.startsWith('pos.')) {
            window.location.href = 'https://kangavena.com';
          } else {
            setIsDashboardMode(false);
            setMode('public');
          }
        }} storeApiKey={apiKey} storeName={storeName} />
      </>
    );
  }

  const isDedicatedSubRoute = 
    window.location.pathname.startsWith('/tenet') || 
    window.location.pathname.startsWith('/corporate') || 
    window.location.pathname.startsWith('/portal') ||
    window.location.pathname.startsWith('/pos') ||
    window.location.pathname === '/privacy' ||
    window.location.pathname === '/terms' ||
    window.location.pathname === '/about' ||
    window.location.pathname === '/returns';

  if (isAdminDashboardMode && !isDedicatedSubRoute) {
    return (
      <>
        <AdminDashboard adminToken={adminToken} onExit={() => {
          sessionStorage.removeItem('adminToken');
          sessionStorage.removeItem('isAdminDashboardMode');
          setIsAdminDashboardMode(false);
          setMode('public');
        }} isPosOnSubdomainEnabled={isPosOnSubdomainEnabled} setIsPosOnSubdomainEnabled={setIsPosOnSubdomainEnabled} />
        {!isLocalhost && <DevBox status={dbStatus} />}
      </>
    );
  }

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAdminLoggingIn) return;
    setAdminLoginError('');
    setIsAdminLoggingIn(true);
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        setAdminToken(data.token);
        sessionStorage.setItem('adminToken', data.token);
        sessionStorage.setItem('isAdminDashboardMode', 'true');
        setIsAdminLoginOpen(false);
        setIsAdminDashboardMode(true);
        setMode('admin');
        setAdminPassword('');
      } else {
        setAdminLoginError(data.error || 'Login failed');
      }
    } catch (err: any) {
      setAdminLoginError(err?.message || 'An error occurred during login. Server may be unreachable.');
    } finally {
      setIsAdminLoggingIn(false);
    }
  };

  const handlePosLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPosLoginError('');
    try {
      const res = await fetch('/api/pos-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(posLoginData)
      });
      const data = await res.json();
      if (data.success) {
        setApiKey(data.store.apiKey);
        setStoreName(data.store.name);
        setIsPosLoginOpen(false);
        setIsDashboardMode(true);
        setMode('store');
        setPosLoginData({ name: '', password: '' });
      } else {
        setPosLoginError(data.error || 'Login failed');
      }
    } catch (err) {
      setPosLoginError('An error occurred during login');
    }
  };

  // Legal/Info pages
  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  if (window.location.pathname === '/terms') {
    return <TermsOfService />;
  }
  if (window.location.pathname === '/about') {
    return <AboutUs />;
  }
  if (window.location.pathname === '/returns') {
    return <ReturnPolicy />;
  }

  // Check if accessing via custom subdomain (e.g. "mybusiness.kangavena.com")
  const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isCustomTenantSubdomain = currentHost.endsWith('.kangavena.com') && 
    !currentHost.startsWith('pos.') && 
    !currentHost.startsWith('admin.') && 
    !currentHost.startsWith('www.') && 
    !currentHost.startsWith('app.') && 
    !currentHost.startsWith('api.');

  // Live Client Portal Route (Headless SaaS Client Portal Renderer & Subdomain Gateway)
  if (isCustomTenantSubdomain || window.location.pathname.startsWith('/portal')) {
    return <LiveClientPortal />;
  }

  // Dedicated Standalone WhatsApp Web Route (Mobile Fast-Lane & PWA Direct Login)
  if (window.location.pathname.startsWith('/whatsapp') || window.location.pathname.startsWith('/wa')) {
    if (tenetToken) {
      return (
        <div className="w-screen h-[100dvh] bg-[#0c1317] overflow-hidden">
          <KangavenaWhatsAppWeb 
            token={tenetToken} 
            standalone={true}
            onLogout={() => {
              localStorage.removeItem('tenet_client_token');
              localStorage.removeItem('tenet_token');
              sessionStorage.removeItem('tenet_client_token');
              sessionStorage.removeItem('tenet_token');
              setTenetToken('');
            }} 
          />
        </div>
      );
    } else {
      return (
        <KangavenaWhatsAppLogin 
          onSuccess={(token) => {
            setTenetToken(token);
          }} 
        />
      );
    }
  }

  // Tenet Client Portal route handling
  if (window.location.pathname.startsWith('/tenet') || (window.location.pathname === '/login' && tenetToken)) {
    if (tenetToken) {
      return (
        <TenetAdminDashboard 
          token={tenetToken} 
          onLogout={() => {
            localStorage.removeItem('tenet_client_token');
            setTenetToken('');
            window.location.href = '/login';
          }} 
        />
      );
    } else {
      return (
        <SystemLoginModal 
          onClose={() => { window.location.href = '/login'; }}
          onLoginSuccess={(type, data) => {
            if (type === 'tenet') {
              setTenetToken(data.token);
              window.history.pushState({}, '', '/tenet');
            } else if (type === 'corporate_admin') {
              setCorporateAdminToken(data.token);
              window.history.pushState({}, '', '/corporate/admin');
            } else if (type === 'corporate_user') {
              setCorporateUserToken(data.token);
              window.history.pushState({}, '', '/corporate');
            } else if (type === 'pos') {
              window.location.href = '/pos';
            } else if (type === 'godmode') {
              setAdminToken(data);
              sessionStorage.setItem('adminToken', data);
              sessionStorage.setItem('isAdminDashboardMode', 'true');
              setIsAdminDashboardMode(true);
              setMode('admin');
            }
          }} 
        />
      );
    }
  }


  // Corporate Firm Admin Dashboard route handling
  if (window.location.pathname.startsWith('/corporate/admin') || (window.location.pathname === '/login' && corporateAdminToken)) {
    if (corporateAdminToken) {
      return (
        <CorporateAdminDashboard 
          token={corporateAdminToken} 
          onLogout={() => {
            localStorage.removeItem('corporate_admin_token');
            setCorporateAdminToken('');
            window.location.href = '/login';
          }} 
        />
      );
    } else {
      return (
        <SystemLoginModal 
          onClose={() => { window.location.href = '/login'; }}
          onLoginSuccess={(type, data) => {
            if (type === 'corporate_admin') {
              setCorporateAdminToken(data.token);
              window.history.pushState({}, '', '/corporate/admin');
            } else if (type === 'corporate_user') {
              setCorporateUserToken(data.token);
              window.history.pushState({}, '', '/corporate');
            } else if (type === 'tenet') {
              setTenetToken(data.token);
              window.history.pushState({}, '', '/tenet');
            } else if (type === 'pos') {
              window.location.href = '/pos';
            } else if (type === 'godmode') {
              setAdminToken(data);
              sessionStorage.setItem('adminToken', data);
              sessionStorage.setItem('isAdminDashboardMode', 'true');
              setIsAdminDashboardMode(true);
              setMode('admin');
            }
          }} 
        />
      );
    }
  }

  // Corporate Client Portal route handling
  if (window.location.pathname.startsWith('/corporate') || (window.location.pathname === '/login' && corporateUserToken)) {
    if (corporateUserToken) {
      return (
        <CorporateClientPortal 
          token={corporateUserToken} 
          onLogout={() => {
            localStorage.removeItem('corporate_user_token');
            setCorporateUserToken('');
            window.location.href = '/login';
          }} 
        />
      );
    } else {
      return (
        <SystemLoginModal 
          onClose={() => { window.location.href = '/login'; }}
          onLoginSuccess={(type, data) => {
            if (type === 'corporate_user') {
              setCorporateUserToken(data.token);
              window.history.pushState({}, '', '/corporate');
            } else if (type === 'corporate_admin') {
              setCorporateAdminToken(data.token);
              window.history.pushState({}, '', '/corporate/admin');
            } else if (type === 'tenet') {
              setTenetToken(data.token);
              window.history.pushState({}, '', '/tenet');
            } else if (type === 'pos') {
              window.location.href = '/pos';
            } else if (type === 'godmode') {
              setAdminToken(data);
              sessionStorage.setItem('adminToken', data);
              sessionStorage.setItem('isAdminDashboardMode', 'true');
              setIsAdminDashboardMode(true);
              setMode('admin');
            }
          }} 
        />
      );
    }
  }

  // System Login route handling
  if (window.location.pathname === '/login' || window.location.pathname === '/register' || isSystemLoginOpen) {
    return (
      <SystemLoginModal 
        initialTab={systemLoginInitialTab}
        onClose={() => {
          setIsSystemLoginOpen(false);
          if (window.location.pathname === '/login' || window.location.pathname === '/register') {
            window.location.href = '/';
          }
        }}
        onLoginSuccess={(type, data) => {
          setIsSystemLoginOpen(false);
          if (type === 'tenet') {
            setTenetToken(data.token);
            window.location.href = '/tenet';
          } else if (type === 'corporate_admin') {
            setCorporateAdminToken(data.token);
            window.location.href = '/corporate/admin';
          } else if (type === 'corporate_user') {
            setCorporateUserToken(data.token);
            window.location.href = '/corporate';
          } else if (type === 'pos') {
            window.location.href = '/pos';
          } else if (type === 'godmode') {
            setAdminToken(data);
            sessionStorage.setItem('adminToken', data);
            sessionStorage.setItem('isAdminDashboardMode', 'true');
            setIsAdminDashboardMode(true);
            setMode('admin');
          }
        }} 
      />
    );
  }

  return (
    <>
      <KangavenaHome 
        onPosLogin={() => {
          setSystemLoginInitialTab('pos');
          setIsSystemLoginOpen(true);
        }} 
        onRegister={() => {
          setSystemLoginInitialTab('tenet_register');
          setIsSystemLoginOpen(true);
        }}
        onAdminLogin={() => setIsAdminLoginOpen(true)} 
      />

      {/* Admin Login Modal */}
      {isAdminLoginOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0a0a0a] border border-cyan-500/30 rounded-2xl p-8 w-full max-w-md shadow-[0_0_50px_rgba(6,182,212,0.2)]">
            <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 uppercase tracking-tighter">Admin Neural Access</h2>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              {adminLoginError && <div className="text-red-400 text-sm font-bold bg-red-500/10 p-3 rounded-lg border border-red-500/20">{adminLoginError}</div>}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Master Admin Password</label>
                <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-cyan-500 outline-none transition-all" placeholder="••••••••" required />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isAdminLoggingIn}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black py-3 rounded-xl transition-all uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                >
                  {isAdminLoggingIn ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Authorizing...
                    </>
                  ) : (
                    'Authorize'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAdminLoginOpen(false)}
                  disabled={isAdminLoggingIn}
                  className="px-6 py-3 border border-white/10 rounded-xl text-white/60 hover:bg-white/5 transition-all text-xs uppercase tracking-widest"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* POS Login Modal */}
      {isPosLoginOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0a0a0a] border border-purple-500/30 rounded-2xl p-8 w-full max-w-md shadow-[0_0_50px_rgba(168,85,247,0.2)]">
            <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-500">POS SYSTEM LOGIN</h2>
            <form onSubmit={handlePosLogin} className="space-y-4">
              {posLoginError && <div className="text-red-400 text-sm font-bold bg-red-500/10 p-3 rounded-lg border border-red-500/20">{posLoginError}</div>}
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Store Name</label>
                <input type="text" value={posLoginData.name} onChange={e => setPosLoginData({...posLoginData, name: e.target.value})} className="w-full bg-black border border-white/10 rounded-lg p-3 text-white focus:border-purple-500 outline-none transition-colors" placeholder="e.g., Tech Hub" required />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Password</label>
                <input type="password" value={posLoginData.password} onChange={e => setPosLoginData({...posLoginData, password: e.target.value})} className="w-full bg-black border border-white/10 rounded-lg p-3 text-white focus:border-purple-500 outline-none transition-colors" placeholder="Enter POS password" required />
              </div>
              <div className="flex justify-end gap-4 mt-8">
                <button type="button" onClick={() => setIsPosLoginOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors font-bold uppercase text-sm">Cancel</button>
                <button type="submit" disabled={!posLoginData.name || !posLoginData.password} className="px-6 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold uppercase text-sm shadow-[0_0_15px_rgba(168,85,247,0.4)] transition-all">Login</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Luna AI Checkout Modal */}
      <CheckoutModal isOpen={!!checkoutData} productName={checkoutData?.productName || ''} originalPrice={checkoutData?.originalPrice} finalPrice={checkoutData?.finalPrice || ''} onConfirm={() => { setCheckoutData(null); }} onClose={() => setCheckoutData(null)} />
    </>
  );
}

